import { useState, useEffect, useMemo, useCallback } from 'react';
import { api } from '../lib/api.js';
import { RecipeCard } from './RecipeCard.jsx';

// Recipe library — Tally-sourced, read-only. A searchable grid of menu items;
// tapping one opens the professional card. Managers get a "Manage list" mode to
// hide items that aren't recipes (bottled drinks, retail) — per item or a whole
// category — so those stop cluttering the library. (RECIPE-CARDS-SPEC.md.)

const FOREST = '#1f4d3a';

export function RecipeCardsView({ user }) {
  const isManager = !!(typeof window !== 'undefined' && window.EmpireRoles?.isManager?.(user));
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [manage, setManage] = useState(false);
  const [openCard, setOpenCard] = useState(null);
  const [openLoading, setOpenLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api('GET', `/api/recipe-cards${manage && isManager ? '?manage=1' : ''}`);
      setItems(r.items || []);
      setError('');
    } catch (e) {
      setError(e.message || 'Could not load recipes');
    } finally {
      setLoading(false);
    }
  }, [manage, isManager]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((i) => `${i.name} ${i.category || ''}`.toLowerCase().includes(q));
  }, [items, query]);

  const byCategory = useMemo(() => {
    const m = new Map();
    for (const i of filtered) {
      const c = i.category || 'Other';
      if (!m.has(c)) m.set(c, { items: [], categoryId: i.category_id, allHidden: true });
      const g = m.get(c);
      g.items.push(i);
      if (!i.hidden) g.allHidden = false;
    }
    return [...m.entries()];
  }, [filtered]);

  async function open(item) {
    setOpenLoading(true);
    try {
      const card = await api('GET', `/api/recipe-cards/${encodeURIComponent(item.square_item_id)}`);
      setOpenCard(card);
    } catch {
      setOpenCard({ name: item.name, category: item.category, photo: item.photo, steps: { hot: [], iced: [] }, sizes: [] });
    } finally {
      setOpenLoading(false);
    }
  }

  async function toggleItem(item) {
    const hide = !item.hidden;
    setItems((prev) => prev.map((x) => x.square_item_id === item.square_item_id ? { ...x, hidden: hide } : x));
    try {
      await api('PUT', `/api/square/catalog/items/${encodeURIComponent(item.square_item_id)}`, { no_recipe_needed: hide ? 1 : 0 });
    } catch {
      setItems((prev) => prev.map((x) => x.square_item_id === item.square_item_id ? { ...x, hidden: !hide } : x)); // revert
    }
  }

  async function toggleCategory(categoryId, hide) {
    if (!categoryId) return;
    try {
      await api('PUT', `/api/square/catalog/categories/${encodeURIComponent(categoryId)}`, { hidden: hide ? 1 : 0 });
      await load();
    } catch { /* keep current view */ }
  }

  if (loading) return <div className="card text-sm text-gray-500">Loading recipes…</div>;
  if (error) return <div className="card" style={{ borderLeft: '4px solid #c62828' }}>
    <div className="text-sm font-semibold text-red-700">Recipes unavailable</div>
    <div className="text-xs text-gray-600 mt-1">{error}</div>
  </div>;

  return (
    <div className="space-y-4">
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', flexWrap: 'wrap', justifyContent: 'space-between' }}>
        <div>
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search recipes…"
                 style={{ width: 'min(360px, 70vw)', padding: '10px 14px', borderRadius: 10,
                           border: '1px solid #d1d5db', fontSize: 14, fontFamily: 'inherit' }} />
          <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 6 }}>
            {manage
              ? 'Managing which items show. Hidden items (bottled drinks, retail) are dimmed — tap to show/hide.'
              : 'From Tally. Ingredients show now; hot/cold steps appear as they’re entered there.'}
          </div>
        </div>
        {isManager && (
          <button onClick={() => setManage((m) => !m)}
                  style={{ fontSize: 12, fontWeight: 700, padding: '8px 14px', borderRadius: 8, cursor: 'pointer',
                            border: `1px solid ${manage ? FOREST : '#d1d5db'}`,
                            background: manage ? FOREST : '#fff', color: manage ? '#fff' : '#374151' }}>
            {manage ? 'Done' : 'Manage list'}
          </button>
        )}
      </div>

      {byCategory.map(([cat, g]) => (
        <div key={cat}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '4px 0 10px' }}>
            <div style={{ fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#6b7280' }}>{cat}</div>
            {manage && g.categoryId && (
              <button onClick={() => toggleCategory(g.categoryId, !g.allHidden)}
                      style={{ fontSize: 11, fontWeight: 600, padding: '2px 10px', borderRadius: 999, cursor: 'pointer',
                                border: '1px solid #d1d5db', background: '#fff', color: '#6b7280' }}>
                {g.allHidden ? 'Show whole category' : 'Hide whole category'}
              </button>
            )}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 12 }}>
            {g.items.map((i) => (
              <div key={i.square_item_id} style={{ position: 'relative', opacity: manage && i.hidden ? 0.45 : 1 }}>
                <button onClick={() => (manage ? toggleItem(i) : open(i))}
                        style={{ textAlign: 'left', width: '100%', border: '1px solid #eceef0', borderRadius: 12, overflow: 'hidden',
                                  background: '#fff', cursor: 'pointer', padding: 0, fontFamily: 'inherit', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                  {i.photo ? (
                    <img src={i.photo} alt="" loading="lazy" style={{ width: '100%', height: 110, objectFit: 'cover', display: 'block' }} />
                  ) : (
                    <div style={{ width: '100%', height: 70, background: `linear-gradient(135deg, ${FOREST}, #2e6b4f)` }} />
                  )}
                  <div style={{ padding: '8px 10px 10px' }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#1a1a1a', lineHeight: 1.25 }}>{i.name}</div>
                    {i.temps?.length > 0 && (
                      <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 3, textTransform: 'capitalize' }}>{i.temps.join(' · ')}</div>
                    )}
                  </div>
                </button>
                {manage && (
                  <div style={{ position: 'absolute', top: 6, right: 6, fontSize: 11, fontWeight: 700, padding: '3px 8px',
                                 borderRadius: 999, background: i.hidden ? 'rgba(0,0,0,0.6)' : FOREST, color: '#fff', pointerEvents: 'none' }}>
                    {i.hidden ? 'Hidden' : 'Shown'}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}

      {filtered.length === 0 && <div className="card text-sm text-gray-500">No recipes match "{query}".</div>}

      {openLoading && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', zIndex: 1000,
                       display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 14 }}>Loading card…</div>
      )}
      {openCard && <RecipeCard card={openCard} onClose={() => setOpenCard(null)} />}
    </div>
  );
}
